import {
  useGetUrl,
  translations as utilsTranslations,
} from '@seek/ca-myprofile-utils';
import { useHubble } from '@seek/hubble';
import { useTranslations } from '@vocab/react';
import { Box, Stack, Button, Text, IconEdit } from 'braid-design-system';
import { useState, useEffect } from 'react';

import { ExternalLink } from 'src/components/ExternalLink/ExternalLink';
import { useAppConfig } from 'src/modules/appConfig';

import translations from './.vocab';

interface Props {
  backToCropStep: () => void;
  navigateToUploadingStep: () => void;
  croppedImage: File | null;
}

export const UploadPreviewStep = ({
  backToCropStep,
  navigateToUploadingStep,
  croppedImage,
}: Props) => {
  const hubble = useHubble();
  const { t } = useTranslations(translations);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const { zone, language } = useAppConfig();
  const getUrl = useGetUrl(zone, language);

  useEffect(() => {
    hubble.trackEvent('component_viewed', {
      currentPageSection: 'photo preview',
      component: {
        type: 'card',
        name: 'photo preview',
      },
      displayLanguage: language,
      zone,
    });
  }, [hubble, language, zone]);

  useEffect(() => {
    if (croppedImage) {
      const objectUrl = URL.createObjectURL(croppedImage);

      setPreviewUrl(objectUrl); // Create and set preview url

      return () => {
        URL.revokeObjectURL(objectUrl); // Revoke object url to free up memory
      };
    }
    setPreviewUrl(null);
  }, [croppedImage]);

  if (!previewUrl) {
    return null;
  }

  const privacyTranslations =
    utilsTranslations.getProfileAvatarDisclaimers(language);

  const handleUploadClick = () => {
    hubble.trackEvent('component_pressed', {
      currentPageSection: 'photo preview',
      component: {
        type: 'button',
        name: 'upload',
      },
      displayLanguage: language,
      zone,
    });

    navigateToUploadingStep();
  };

  const handleEditClick = () => {
    hubble.trackEvent('component_pressed', {
      currentPageSection: 'photo preview',
      component: {
        type: 'button',
        name: 'edit',
      },
      displayLanguage: language,
      zone,
    });

    backToCropStep();
  };

  const trackPreviewLegalLink = (
    name: 'privacy policy' | 'collection notice' | 'community guidelines',
  ) => {
    hubble.trackEvent('component_pressed', {
      currentPageSection: 'photo preview',
      component: {
        type: 'textLink',
        name,
      },
      displayLanguage: language,
      zone,
    });
  };

  return (
    <Stack space="medium" align="center">
      <Box style={{ width: 256, height: 256 }}>
        <img
          src={previewUrl}
          alt="Profile avatar preview"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '8px',
          }}
        />
      </Box>
      <Text size="small">
        {t('Ready to go! Your photo will look like this.')}
      </Text>
      <Box width="full">
        <Stack space="xsmall">
          <Button tone="brandAccent" onClick={handleUploadClick}>
            {t('Upload')}
          </Button>
          <Box display="flex" justifyContent="center">
            <Button
              tone="formAccent"
              variant="soft"
              onClick={handleEditClick}
              icon={<IconEdit />}
            >
              {t('Edit')}
            </Button>
          </Box>
        </Stack>
      </Box>
      <Stack space="small">
        <Text size="small" tone="secondary">
          {privacyTranslations[
            'By uploading, you agree to our Privacy Policy, Collection Notice and Community Guidelines. [with links]'
          ].format({
            PrivacyPolicyLink: (v) => (
              <ExternalLink
                key="privacy-policy-link"
                target="_blank"
                rel="noopener noreferrer"
                href={getUrl('privacy-policy')}
                weight="weak"
                onClick={() => trackPreviewLegalLink('privacy policy')}
              >
                {v}
              </ExternalLink>
            ),
            CollectionNoticeLink: (v) => (
              <ExternalLink
                key="collection-notice-link"
                target="_blank"
                rel="noopener noreferrer"
                href={getUrl('collection-notice')}
                weight="weak"
                onClick={() => trackPreviewLegalLink('collection notice')}
              >
                {v}
              </ExternalLink>
            ),
            CommunityGuidelinesLink: (v) => (
              <ExternalLink
                key="community-guidelines-link"
                target="_blank"
                rel="noopener noreferrer"
                href={getUrl('community-guidelines')}
                weight="weak"
                onClick={() => trackPreviewLegalLink('community guidelines')}
              >
                {v}
              </ExternalLink>
            ),
          })}
        </Text>
      </Stack>
    </Stack>
  );
};
